import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { LinkDirective } from '../../../_shared/directives/link.directive';
import { ICardCatalog } from '../../../_shared/models/card/card-catalog.model';
import { ICardComponent } from '../../../_shared/models/card/card.model';
import { IChip } from '../../../_shared/models/chip.model';
import { ImageData } from '../../../_shared/models/image/image-data.model';
import { EMPTY_LINK, ILink } from '../../../_shared/models/link.model';
import { MediaImage } from '../../../_shared/models/media/media-image.model';
import { IProductProperty } from '../../../_shared/models/product/product-information.model';
import { LinkService } from '../../../_shared/services/link.service';
import { SnowplowService } from '../../../_shared/services/snowplow.service';
import { CardContainer } from '../../../_shared/types/card/card-container.type';
import { withConfigFallback } from '../../../_shared/utils/with-config-fallback';
import { ChipsComponent } from '../../../chip/chips.component';
import { HeadingComponent } from '../../../heading/heading.component';
import { IconComponent } from '../../../icon/icon.component';
import { ImageItemComponent } from '../../../media/media-item/image-item/image-item.component';
import { TextHtmlComponent } from '../../../text-html/text-html.component';

@Component({
    selector: 'sirius-card-catalog',
    templateUrl: './card-catalog.component.html',
    styleUrl: './card-catalog.component.scss',
    imports: [
        NgTemplateOutlet,
        LinkDirective,
        ImageItemComponent,
        HeadingComponent,
        ChipsComponent,
        IconComponent,
        TextHtmlComponent,
        TranslateModule,
    ],
    host: {
        // Keeps the card light on dark sections.
        'class': 'sir-card-catalog sir-text-style-paragraph-m-default is-light',
        '[class.has-image]': 'hasImage()',
        '[class.has-attributes]': 'hasAttributes()',
        '[class.has-properties]': 'hasProperties()',
        '[class.is-linked]': 'hasLink()',
    },
})
export class CardCatalogComponent implements ICardComponent<ICardCatalog> {
    private readonly snowplowService = inject(SnowplowService);
    private readonly linkService = inject(LinkService);

    readonly config = input<ICardCatalog | undefined>(undefined);
    readonly imageData = input<ImageData | undefined>(undefined);
    readonly container = input<CardContainer>('slider');

    readonly title = input('');
    readonly teaserHtml = input('');
    readonly image = input<MediaImage | undefined>(undefined);
    readonly attributes = input<IChip[]>([]);
    readonly properties = input<IProductProperty[]>([]);
    readonly link = input<ILink>(EMPTY_LINK);

    protected readonly resolvedTitle = withConfigFallback<ICardCatalog, 'title', string>(
        this.config,
        'title',
        this.title
    );
    protected readonly resolvedTeaserHtml = withConfigFallback<ICardCatalog, 'teaserHtml', string>(
        this.config,
        'teaserHtml',
        this.teaserHtml
    );
    protected readonly resolvedImage = withConfigFallback<ICardCatalog, 'image'>(this.config, 'image', this.image);
    protected readonly resolvedAttributes = withConfigFallback<ICardCatalog, 'attributes', IChip[]>(
        this.config,
        'attributes',
        this.attributes
    );
    protected readonly resolvedProperties = withConfigFallback<ICardCatalog, 'properties', IProductProperty[]>(
        this.config,
        'properties',
        this.properties
    );
    protected readonly resolvedLink = withConfigFallback<ICardCatalog, 'link', ILink>(this.config, 'link', this.link);

    protected readonly hasImage = computed(() => !!this.resolvedImage());
    protected readonly hasAttributes = computed(() => this.resolvedAttributes().length > 0);
    protected readonly hasProperties = computed(() => this.resolvedProperties().length > 0);
    protected readonly hasLink = computed(() => !!this.resolvedLink().href);
    protected readonly showCta = computed(() => {
        const linkTitle = this.resolvedLink().title?.trim();
        return this.hasLink() && !!linkTitle && linkTitle !== this.resolvedTitle().trim();
    });

    readonly selectEvent = output<void>();

    protected onClick(): void {
        this.trackLinkClick();
        this.selectEvent.emit();
    }

    private trackLinkClick(): void {
        const link = this.resolvedLink();

        this.snowplowService.track('linkClick', {
            category: 'card',
            container: this.container(),
            identifier: link.tracking?.parameters['identifier'],
            target_url: this.linkService.getFullPath(link.href),
            element_content: link.title,
            outbound: link.target === '_blank',
            context: [],
        });
    }
}
